import React, { ComponentType, ReactNode, useMemo, useState } from 'react';
import './Subscription.css';
import { priceCurrencyToString } from '../../constants/payment';
import {
  BUY_SUBSCRIPTION_NOW_MSG,
  DONT_HAVE_SUBSCRIPTION_PATIENT,
  RENEW_SUBSCRIPTION_MSG,
  SUBSCRIPTION_BLOCKED_MSG,
  SUBSCRIPTION_IS_ACTIVE,
  SUBSCRIPTION_PENDING_MSG,
  SUBSCRIPTON_IS_EXPIRED_PATIENT,
  TO_MANAGE_APPLE_SUBSCRIPTION_USE_APP_STORE_MSG,
  USER_ENROLLED_ON_FREE_TRIAL,
} from '../../constants/localization';
import { UserRoleType } from '../../constants/types';
import PaymentModal from '../../components/Modals/Payment';
import { INDEX_ROUTE_BY_ROLE } from '../../constants/routes';
import { isAndroid } from 'react-device-detect';
import { MOBILE_LINK } from '../../pages/Landing/LnMenHealth/constants';
import { useQuery } from 'react-apollo';
import {
  GET_USER_FREE_TRIAL_DETAILS,
  UNIVERSAL_USER_QUERY,
} from '../../constants/gql/me';
import { USER_ACCESS } from '../../constants/gql/payment';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { CUSTOM_DATE_FNS_FORMAT } from '../../constants/ui';

const CHECK_MARK = (
  <svg
    width="16"
    height="12"
    viewBox="0 0 16 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.38889 12L0 6.61111L1.41667 5.19444L5.38889 9.16667L14.5833 0L16 1.41667L5.38889 12Z"
      fill="#0D329E"
    />
  </svg>
);

const DEFAULT_PATIENT_DESCRIPTION =
  'MenHealth and Sunrise capture vital data that enables early diagnostics, and empowers patients to self-assess, be more aware, and have more control of their urinary health. MenHealth® and Sunrise® "listen" to the sound of urine when it hits the water surface in a toilet and calculate all the urine flow parameters. Includes access to personal monitoring dashboard and MenHealth® and Sunrise® Smart Phone Uroflowmeter Apps (Android or iPhone). Share accurate test results with your doctor to help diagnose and manage your urinary health.';

export const SubscriptionCardSkeleton: React.FC = () => (
  <div className="subscription-card-wrapper">
    <div className="subscription-card subscription-card--skeleton">
      <div className="subscription-card__status subscription-card__status--skeleton">
        <div className="subscription-card__skeleton-line subscription-card__skeleton-status" />
      </div>

      <div className="subscription-card__header">
        <div className="subscription-card__skeleton-line subscription-card__skeleton-price" />
        <div className="subscription-card__skeleton-line subscription-card__skeleton-title" />
        <div className="subscription-card__skeleton-line subscription-card__skeleton-subtitle" />
      </div>

      <div className="subscription-card__skeleton-text">
        <div className="subscription-card__skeleton-line" />
        <div className="subscription-card__skeleton-line short" />
      </div>

      <div className="subscription-card__features">
        <div className="subscription-card__feature">
          <div className="subscription-card__skeleton-bullet" />
          <div className="subscription-card__skeleton-line" />
        </div>
        <div className="subscription-card__feature">
          <div className="subscription-card__skeleton-bullet" />
          <div className="subscription-card__skeleton-line" />
        </div>
        <div className="subscription-card__feature">
          <div className="subscription-card__skeleton-bullet" />
          <div className="subscription-card__skeleton-line short" />
        </div>
        <div className="subscription-card__feature">
          <div className="subscription-card__skeleton-bullet" />
          <div className="subscription-card__skeleton-line medium" />
        </div>
      </div>

      <div className="subscription-card__trial-info subscription-card__trial-info--skeleton">
        <div className="subscription-card__skeleton-line subscription-card__skeleton-trial-title" />
        <div className="subscription-card__skeleton-line" />
        <div className="subscription-card__skeleton-line short" />
      </div>

      <div className="subscription-card__actions">
        <div className="subscription-card__skeleton-button" />
        <div className="subscription-card__skeleton-button outline" />
      </div>

      <div className="subscription-card__disclaimer subscription-card__disclaimer--skeleton">
        <div className="subscription-card__skeleton-line disclaimer" />
      </div>
    </div>
  </div>
);

type SecondaryAction = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
};

type PaymentPlanStatusTone = 'info' | 'success' | 'warning' | 'danger';

const PaymentModalComponent = PaymentModal as unknown as ComponentType<any>;

type PatientPlanCardProps = {
  planId: string;
  price: string;
  currency: string;
  role: UserRoleType;
  planTitle: string;
  planSubtitle?: string;
  description?: string;
  features: string[];
  isActivePlan: boolean;
  isPendingPlan: boolean;
  isRenewableSubscription: boolean;
  isBlockedPlan: boolean;
  isIOSSubscription: boolean;
  chooseDisabled: boolean;
  label?: string;
  maxPatients: number;
  switchAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  secondaryActions?: SecondaryAction[];
  disclaimer?: string;
  extraContent?: ReactNode;
  forceEnableRenewal?: boolean;
};

const getStatusMessage = (
  props: Pick<
    PatientPlanCardProps,
    'isActivePlan' | 'isPendingPlan' | 'isRenewableSubscription' | 'isBlockedPlan'
  >,
): { tone: PaymentPlanStatusTone; message: string } => {
  if (props.isBlockedPlan) {
    return { tone: 'danger', message: SUBSCRIPTION_BLOCKED_MSG };
  }
  if (props.isPendingPlan) {
    return { tone: 'warning', message: SUBSCRIPTION_PENDING_MSG };
  }
  if (props.isRenewableSubscription) {
    return { tone: 'warning', message: SUBSCRIPTON_IS_EXPIRED_PATIENT };
  }
  if (props.isActivePlan) {
    return { tone: 'success', message: SUBSCRIPTION_IS_ACTIVE };
  }
  return { tone: 'info', message: DONT_HAVE_SUBSCRIPTION_PATIENT };
};

const PatientPlanCard: React.FC<PatientPlanCardProps> = ({
  planId,
  price,
  currency,
  role,
  planTitle,
  planSubtitle = 'Patient Self Monitoring Plan',
  description = DEFAULT_PATIENT_DESCRIPTION,
  features,
  isActivePlan,
  isPendingPlan,
  isRenewableSubscription,
  isBlockedPlan,
  isIOSSubscription,
  chooseDisabled,
  label,
  maxPatients,
  switchAction,
  secondaryActions = [],
  disclaimer,
  extraContent,
  forceEnableRenewal = false,
}) => {
  const history = useHistory();
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);

  const { data: freeTrialData, loading: freeTrialLoading, refetch } = useQuery(
    GET_USER_FREE_TRIAL_DETAILS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const {
    data: userAccessData,
    loading: userAccessLoading,
    error: userAccessError,
  } = useQuery(USER_ACCESS, {
    fetchPolicy: 'network-only',
  });

  const { loading: profileLoading } = useQuery(UNIVERSAL_USER_QUERY, {
    fetchPolicy: 'network-only',
  });

  const toDate = (value: any): Date | null => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    const candidate = new Date(value);
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  };

  const isFreeTrial = Boolean(
    freeTrialData?.profile?.isManualSubscribed &&
      freeTrialData?.profile?.manualSubscribedTillDate,
  );

  const status = useMemo(
    () => {
      if (isFreeTrial) {
        return {
          tone: 'success',
          message: USER_ENROLLED_ON_FREE_TRIAL(
            toDate(freeTrialData?.profile?.manualSubscribedTillDate) || undefined,
          ),
        };
      }

      return getStatusMessage({
        isActivePlan,
        isPendingPlan,
        isRenewableSubscription,
        isBlockedPlan,
      });
    },
    [
      isActivePlan,
      isPendingPlan,
      isRenewableSubscription,
      isBlockedPlan,
      isFreeTrial,
      freeTrialData?.profile?.manualSubscribedTillDate,
    ],
  );

  if (userAccessError) {
    console.error(userAccessError);
  }

  React.useEffect(() => {
    refetch().catch((error) => {
      console.error('Failed to refetch free trial details', error);
    });
  }, [refetch]);

  const isDataLoading = freeTrialLoading || userAccessLoading || profileLoading;
  const hasAnyData = freeTrialData || userAccessData;
  
  if (isDataLoading && !hasAnyData) {
    return <SubscriptionCardSkeleton />;
  }
  
  const isDoctorSubscriptionActive =
    userAccessData?.getUserAccess?.underDoctorSubscription?.isActive;
  const isClinicSubscriptionActive =
    userAccessData?.getUserAccess?.underClinicSubscription?.isActive;

  const disableSubscriptionActions = forceEnableRenewal
    ? false
    : isFreeTrial || isDoctorSubscriptionActive || isClinicSubscriptionActive;

  const primaryButtonVisible =
    !isActivePlan &&
    !isIOSSubscription &&
    (!isRenewableSubscription || role === 'PATIENT') &&
    !isFreeTrial;

  const primaryButtonLabel = isRenewableSubscription
    ? RENEW_SUBSCRIPTION_MSG
    : BUY_SUBSCRIPTION_NOW_MSG;

  const openPaymentModal = () => {
    if (disableSubscriptionActions || chooseDisabled) {
      return;
    }
    setPaymentModalOpen(true);
  };

  const closePaymentModal = (value: boolean) => {
    setPaymentModalOpen(value);
  };

  const successfullyPaymentRedirect = () => {
    history.replace(INDEX_ROUTE_BY_ROLE(role));
    if (isAndroid && role === 'PATIENT') {
      const mobileAppLinkURL: URL = new URL(MOBILE_LINK);
      window.open(mobileAppLinkURL.href, '_self');
    }
  };

  const cardClassNames = ['subscription-card'];
  if (status.tone) {
    cardClassNames.push(`subscription-card--${status.tone}`);
  }

  const trialEndsAt = toDate(freeTrialData?.profile?.manualSubscribedTillDate);

  const cardLabel = label || (isFreeTrial ? 'Trial' : undefined);

  const trialUpgradeDisabled = disableSubscriptionActions || chooseDisabled;
  const hasSwitchAction = Boolean(switchAction);
  const hasSecondaryActions = secondaryActions.length > 0;
  const shouldShowActions =
    primaryButtonVisible ||
    hasSwitchAction ||
    hasSecondaryActions ||
    (!!disclaimer && !isFreeTrial);

  return (
    <div className="subscription-card-wrapper">


      <div className={cardClassNames.join(' ')}>
        {cardLabel && (
          <div className="subscription-card__badge">
            <span>{cardLabel}</span>
          </div>
        )}

        <div className="subscription-card__header">
          <div className="subscription-card__price">
            {priceCurrencyToString(price, currency)}
            <span className="subscription-card__price-period">/mo</span>
          </div>
          <h1 className="subscription-card__title">{planTitle}</h1>
          <div className="subscription-card__subtitle">{planSubtitle}</div>
        </div>

        {description && (
          <p className="subscription-card__description">{description}</p>
        )}

        {features?.length > 0 && (
          <div className="subscription-card__features">
            {features.map((feature, index) => (
              <div
                className="subscription-card__feature"
                key={`${feature}-${index}`}
              >
                {CHECK_MARK}
                <span>{feature}</span>
              </div>
            ))}
          </div>
        )}

        {isIOSSubscription && (
          <div className="subscription-card__ios-note">
            {TO_MANAGE_APPLE_SUBSCRIPTION_USE_APP_STORE_MSG}
          </div>
        )}

        {extraContent && (
          <div className="subscription-card__extra">{extraContent}</div>
        )}

        {isFreeTrial && (
          <div className="subscription-card__trial-info">
            <div className="subscription-card__trial-title">Trial access</div>
            <p className="subscription-card__trial-text">
              {trialEndsAt
                ? `You can enjoy all MenHealth features until ${format(
                    trialEndsAt,
                    CUSTOM_DATE_FNS_FORMAT,
                  )}.`
                : 'You are enjoying full access during your free trial.'}
            </p>
            <p className="subscription-card__trial-text">
              Continue with a paid plan at any time to keep your data and
              insights once the trial ends.
            </p>
            <button
              type="button"
              className="subscription-card__primary-action subscription-card__primary-action--outline"
              disabled={trialUpgradeDisabled}
              onClick={openPaymentModal}
            >
              Upgrade to paid plan
            </button>
          </div>
        )}

        {shouldShowActions && (
          <div className="subscription-card__actions">
            {primaryButtonVisible && (
              <button
                type="button"
                className="subscription-card__primary-action"
                disabled={disableSubscriptionActions || chooseDisabled}
                onClick={openPaymentModal}
              >
                {primaryButtonLabel}
              </button>
            )}

            {hasSwitchAction && (
              <button
                type="button"
                className="subscription-card__secondary-action"
                disabled={switchAction.disabled || disableSubscriptionActions}
                onClick={switchAction.onClick}
              >
                {switchAction.label}
              </button>
            )}

            {secondaryActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={`subscription-card__secondary-action${
                  action.tone === 'danger'
                    ? ' subscription-card__secondary-action--danger'
                    : ''
                }`}
                disabled={action.disabled || disableSubscriptionActions}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}

            {disclaimer && !isFreeTrial && (
              <div className="subscription-card__disclaimer">{disclaimer}</div>
            )}
          </div>
        )}
      </div>

      <PaymentModalComponent
        isModalOpen={isPaymentModalOpen}
        toggleModal={closePaymentModal}
        planId={planId}
        price={price}
        currency={currency}
        header={planTitle}
        isPatient={role === 'PATIENT'}
        maxPatients={maxPatients}
        successfullyPaymentRedirect={successfullyPaymentRedirect}
        hasActivePlan={isActivePlan}
        isRenewedSubscription={isRenewableSubscription}
        isPendingSubscription={isPendingPlan}
      />
    </div>
  );
};

export default PatientPlanCard;